// client/src/pages/HowItWorks.js
import React from 'react';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    num: '01',
    title: 'Create Your Free Account',
    desc: 'Sign up in under 60 seconds. No credit card required. Your free account gives you full access to search and browse 574,000+ clinical studies from ClinicalTrials.gov.',
    icon: '👤'
  },
  {
    num: '02',
    title: 'Search the Database',
    desc: 'Type any condition, disease, drug name, or NCT ID into the search bar. Our autocomplete suggests matching conditions as you type, so you find the right term faster.',
    icon: '🔍'
  },
  {
    num: '03',
    title: 'Filter Your Results',
    desc: 'Narrow down results using powerful filters: study status, phase, age group, study type, funder, location, date ranges, sex, and more — all mirroring ClinicalTrials.gov\'s own filter set.',
    icon: '⚙️'
  },
  {
    num: '04',
    title: 'Explore Study Details',
    desc: 'Click any study to see a full breakdown: eligibility criteria, interventions, outcome measures, locations, enrollment numbers, and links to published results.',
    icon: '📋'
  },
  {
    num: '05',
    title: 'Save & Export (Pro)',
    desc: 'Pro users can save unlimited studies, save search presets, and export results to CSV with up to 1,000 studies per export — ideal for researchers and teams.',
    icon: '💾'
  }
];

const features = [
  { icon: '⚡', title: 'Live Data', desc: 'All results come directly from ClinicalTrials.gov API in real time. Never stale, never cached.' },
  { icon: '🔒', title: 'HIPAA-Aware', desc: 'We never store your search queries on third-party servers. Your research stays private.' },
  { icon: '🌍', title: '50+ Countries', desc: 'Studies span the entire globe — filter by city, state, or country to find trials near you.' },
  { icon: '📊', title: 'All Study Types', desc: 'Interventional, observational, expanded access, patient registries — every type is searchable.' },
  { icon: '🧪', title: 'All Phases', desc: 'From early Phase 1 safety trials to large Phase 4 post-market studies.' },
  { icon: '📥', title: 'CSV Export', desc: 'Pro users can download up to 1,000 studies per search as a clean CSV for analysis.' },
];

export default function HowItWorks() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      {/* Hero */}
      <div style={{ background: 'var(--ink)', color: 'var(--paper)', padding: '64px 24px 56px', textAlign: 'center' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '3px', color: 'var(--muted-2)', textTransform: 'uppercase', marginBottom: 16 }}>
          Documentation
        </div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(32px, 5vw, 54px)', lineHeight: 1.08, marginBottom: 16 }}>
          How BioMedArticles Works
        </h1>
        <p style={{ color: 'var(--muted-2)', fontSize: 16, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
          A faster, cleaner way to search the world's largest clinical trials database.
        </p>
      </div>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 24px 80px' }}>

        {/* Steps */}
        <div style={{ marginTop: 64 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8, color: 'var(--ink)' }}>Getting Started</h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 40, lineHeight: 1.6 }}>Five steps from signup to insights.</p>

          {steps.map((step, i) => (
            <div key={i} className="step-card" style={{ display: 'flex', gap: 28, marginBottom: 36, padding: '28px 32px', background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, alignItems: 'flex-start' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '1px', minWidth: 28, marginTop: 4 }}>{step.num}</div>
              <div style={{ fontSize: 28, flexShrink: 0 }}>{step.icon}</div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, lineHeight: 1.3, color: 'var(--ink)' }}>{step.title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Features grid */}
        <div style={{ marginTop: 64 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8, color: 'var(--ink)' }}>Platform Features</h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 36, lineHeight: 1.6 }}>Built specifically for researchers, clinicians, and patients.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {features.map((f, i) => (
              <div key={i} style={{ background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0, padding: '24px 24px' }}>
                <div style={{ fontSize: 24, marginBottom: 10 }}>{f.icon}</div>
                <h4 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--ink)' }}>{f.title}</h4>
                <p style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Search tips */}
        <div className="search-tips-box" style={{ marginTop: 64, background: 'var(--ink)', color: 'var(--paper)', borderRadius: 0, padding: '36px 40px' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 26, marginBottom: 24 }}>Search Tips</h2>
          <div className="search-tips-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
            {[
              { tip: 'Search by NCT ID', detail: 'If you have a study ID like NCT04280705, paste it directly in the search bar to jump straight to that study.' },
              { tip: 'Use condition names', detail: 'Type "Type 2 Diabetes" instead of just "diabetes" for more precise results. Use autocomplete to pick the exact MeSH term.' },
              { tip: 'Combine filters', detail: 'Stack multiple filters — e.g. RECRUITING + Phase 3 + United States — to narrow down to the most relevant studies.' },
              { tip: 'Pro: Essie expressions', detail: 'Advanced users can enter Essie query expressions like AREA[MinimumAge]RANGE[MIN, 18 years] for fine-grained filtering.' },
            ].map((item, i) => (
              <div key={i}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-2)', letterSpacing: '1.5px', marginBottom: 6 }}>TIP {String(i+1).padStart(2,'0')}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{item.tip}</div>
                <div style={{ fontSize: 13, color: 'var(--muted-2)', lineHeight: 1.6 }}>{item.detail}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Free vs Pro */}
        <div style={{ marginTop: 64 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 8, color: 'var(--ink)' }}>Free vs Pro</h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 32, lineHeight: 1.6 }}>Start free, upgrade when you need more.</p>
          <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              {
                plan: 'Free', price: '$0', color: 'var(--card)', border: 'var(--line-card)',
                items: ['Search all 574,000+ studies', 'All filters', 'Full study details', 'Save up to 5 studies', 'View results & locations']
              },
              {
                plan: 'Pro', price: '$9/mo', color: 'var(--ink)', border: 'var(--ink)', textColor: 'var(--paper)',
                items: ['Everything in Free', 'Unlimited saved studies', 'CSV export (up to 1,000)', 'Save up to 20 search presets', 'Advanced Essie expressions', 'Priority access']
              }
            ].map((p, i) => (
              <div key={i} style={{ background: p.color, border: `${p.textColor ? '2px' : '1px'} solid ${p.border}`, borderRadius: 0, padding: '28px 28px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: p.textColor || 'var(--muted-2)', letterSpacing: '2px', marginBottom: 8 }}>{p.plan.toUpperCase()}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: p.textColor || 'var(--ink)', marginBottom: 20 }}>{p.price}</div>
                {p.items.map((item, j) => (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 14, color: p.textColor || 'var(--muted)' }}>
                    <span style={{ color: p.textColor ? 'var(--muted-2)' : '#16a34a', fontWeight: 700 }}>✓</span> {item}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 56, textAlign: 'center', padding: '48px', background: 'var(--card)', border: '1px solid var(--line-card)', borderRadius: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, marginBottom: 10, color: 'var(--ink)' }}>Ready to start searching?</h2>
          <p style={{ color: 'var(--muted)', fontSize: 15, marginBottom: 28 }}>Create your free account and access 574,000+ clinical trials instantly.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{ background: 'var(--ink)', color: 'var(--paper)', border: 'none', padding: '13px 32px', fontSize: 15, fontWeight: 700, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/register')}>
              Create free account →
            </button>
            <button style={{ background: 'transparent', color: 'var(--ink)', border: '1.5px solid var(--ink)', padding: '13px 32px', fontSize: 14, fontWeight: 500, borderRadius: 0, cursor: 'pointer' }} onClick={() => navigate('/faq')}>
              Read the FAQ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}